/**
 * UnderservedMarket — Hybrid LIVE + NOT_CONNECTED
 *
 * LIVE: underserved_bands (score band distribution from lender_underserved_v)
 * NOT_CONNECTED: credit_invisible, first_time_borrowers, loan_gap, capacity_utilization,
 *                population growth, gap analysis table, demographics
 */
import React from 'react';
import {
  FiUsers,
  FiCreditCard,
  FiDollarSign,
  FiActivity,
  FiLoader,
} from 'react-icons/fi';
import UnderservedPopulationGrowthChart from './UnderservedPopulationGrowthChart';
import AverageLoanGapTrendChart from './AverageLoanGapTrendChart';
import LoanGapByAmountRangeChart from './LoanGapByAmountRangeChart';
import BorrowerCapacityUtilizationChart from './BorrowerCapacityUtilizationChart';
import UnderservedDemographicsChart from './UnderservedDemographicsChart';
import LiveMetricCard from './LiveMetricCard';
import DataSourceBadge from './DataSourceBadge';
import DataSourceNotConnected from './DataSourceNotConnected';
import { useInsightsData } from '../../../hooks/useInsightsData';
import { NOT_CONNECTED_REGISTRY } from '../../../types/insightsTypes';

const UnderservedMarket: React.FC = () => {
  const { data, loading, error } = useInsightsData();
  const us = data?.underserved;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <FiLoader className="animate-spin text-slate-400 mr-3" size={20} />
        <span className="text-slate-500">Loading underserved market data…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-700 font-medium">Failed to load insights data</p>
        <p className="text-red-500 text-sm mt-1">{error.message}</p>
      </div>
    );
  }

  if (!us) return null;

  const hasBands = us.underserved_bands.length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-base sm:text-lg font-medium text-slate-700 mb-4">
          Measure impact on credit invisible and first-time borrowers
        </h3>

        {/* Key Metrics Grid — all NOT_CONNECTED */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <LiveMetricCard
            title="Credit Invisible Served"
            metric={us.credit_invisible_count}
            icon={<FiUsers className="text-slate-600" size={20} />}
            format="number"
            requiredIntegration="los"
          />
          <LiveMetricCard
            title="First-time Borrowers"
            metric={us.first_time_borrowers}
            icon={<FiCreditCard className="text-slate-600" size={20} />}
            format="number"
            requiredIntegration="los"
          />
          <LiveMetricCard
            title="Avg Loan Gap"
            metric={us.avg_loan_gap}
            icon={<FiDollarSign className="text-slate-600" size={20} />}
            format="currency"
            requiredIntegration="los"
          />
          <LiveMetricCard
            title="Capacity Utilization"
            metric={us.capacity_utilization}
            icon={<FiActivity className="text-slate-600" size={20} />}
            format="percent"
            requiredIntegration="los"
          />
        </div>
      </div>

      {/* Underserved Score Band Distribution — LIVE */}
      {hasBands && (
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-base sm:text-lg font-medium text-slate-700">
              Underserved Score Band Distribution
            </h4>
            <DataSourceBadge status="live" source="lender_underserved_v" compact />
          </div>
          <div className="overflow-x-auto scrollbar-hide">
            <table className="w-full min-w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-slate-700 text-xs sm:text-sm">Score Band</th>
                  <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-slate-700 text-xs sm:text-sm">Members</th>
                  <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-slate-700 text-xs sm:text-sm">Avg Score</th>
                  <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-slate-700 text-xs sm:text-sm">Avg DTI</th>
                  <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-slate-700 text-xs sm:text-sm">Avg Reserves</th>
                </tr>
              </thead>
              <tbody>
                {us.underserved_bands.map((band, index) => (
                  <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 sm:py-4 px-2 sm:px-4 font-medium text-slate-800 text-xs sm:text-sm">{band.band}</td>
                    <td className="py-3 sm:py-4 px-2 sm:px-4 text-slate-600 text-xs sm:text-sm">{band.member_count.toLocaleString()}</td>
                    <td className="py-3 sm:py-4 px-2 sm:px-4 text-slate-600 text-xs sm:text-sm">{Math.round(band.avg_score * 10) / 10}</td>
                    <td className="py-3 sm:py-4 px-2 sm:px-4 text-slate-600 text-xs sm:text-sm">
                      {band.avg_dti != null ? `${Math.round(band.avg_dti * 10) / 10}%` : '—'}
                    </td>
                    <td className="py-3 sm:py-4 px-2 sm:px-4 text-slate-600 text-xs sm:text-sm">
                      {band.avg_reserves != null ? `$${Math.round(band.avg_reserves).toLocaleString()}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Population Growth (LOS) & Loan Gap Trend (LOS) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
          <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Underserved Population Growth</h4>
          <UnderservedPopulationGrowthChart />
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
          <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Average Loan Gap Trend</h4>
          <AverageLoanGapTrendChart />
        </div>
      </div>

      {/* Loan Gap Range (LOS) & Capacity Utilization (LOS) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
          <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Loan Gap by Amount Range</h4>
          <LoanGapByAmountRangeChart />
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
          <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Borrower Capacity Utilization</h4>
          <BorrowerCapacityUtilizationChart />
        </div>
      </div>

      {/* Demographics (Survey) */}
      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
        <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Underserved Demographics Performance</h4>
        <UnderservedDemographicsChart />
      </div>

      {/* Gap Analysis Table — NOT_CONNECTED (LOS) */}
      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
        <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Detailed Gap Analysis by Loan Range</h4>
        <DataSourceNotConnected
          info={NOT_CONNECTED_REGISTRY.los}
          metricName="Gap Analysis Table"
        />
      </div>
    </div>
  );
};

export default UnderservedMarket;